import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiService } from '../../services/api';

type CatalogItem = {
  id: number;
  slug: string;
  title: string;
  disease_area?: string | null;
  line_of_therapy?: string | null;
  version_label?: string | null;
  version_number?: number;
  is_published?: boolean;
  created_at?: string | null;
  indication_name?: string | null;
  row_count?: number;
};

type IndicationOption = { id: string; name: string };

function formatUploadedAt(value?: string | null): string {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function uniqueSorted(values: Array<string | null | undefined>): string[] {
  return Array.from(
    new Set(values.map((v) => (v || '').trim()).filter(Boolean)),
  ).sort((a, b) => a.localeCompare(b));
}

async function peekCalendarCsvHints(file: File): Promise<{
  title?: string;
  suggestedLine?: string;
  suggestedSlug?: string;
}> {
  const text = await file.text();
  const first = text.split(/\r?\n/).slice(0, 4).join('\n');
  const titleMatch = first.match(/^"?([^"\n]+)/);
  const title = (titleMatch?.[1] || '').replace(/\s*[—–].*$/, '').trim();
  let suggestedLine = '';
  const lineMatch = title.match(/^(\d+\s*L\s*\+?)/i) || title.match(/\b(\d+\s*L\s*\+?)\b/i);
  if (lineMatch) suggestedLine = lineMatch[1].replace(/\s+/g, '');
  const suggestedSlug = title
    .toLowerCase()
    .replace(/catalyst\s*calendar/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 48);
  return { title, suggestedLine, suggestedSlug };
}

const ManageCatalystCalendars: React.FC = () => {
  const [items, setItems] = useState<CatalogItem[]>([]);
  const [indications, setIndications] = useState<IndicationOption[]>([]);
  const [loadingList, setLoadingList] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [visibilityBusyId, setVisibilityBusyId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [viewUrl, setViewUrl] = useState<string | null>(null);

  const [calendarFile, setCalendarFile] = useState<File | null>(null);
  const [slug, setSlug] = useState('');
  const [diseaseArea, setDiseaseArea] = useState('');
  const [lineOfTherapy, setLineOfTherapy] = useState('');
  const [versionLabel, setVersionLabel] = useState('');
  const [indicationName, setIndicationName] = useState('');
  const [linkTrials, setLinkTrials] = useState(true);

  const loadList = async () => {
    try {
      setLoadingList(true);
      const [listRes, indRes] = await Promise.all([
        apiService.fetchEmergingCalendars({ includeAll: true }),
        apiService.fetchEmergingIndications(),
      ]);
      if (listRes?.success) setItems(listRes.data || []);
      setIndications(
        (indRes || []).map((ind: { id: string; name: string }) => ({
          id: String(ind.id),
          name: ind.name,
        })),
      );
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load calendars');
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => {
    loadList();
  }, []);

  const diseaseAreaOptions = useMemo(
    () => uniqueSorted(items.map((i) => i.disease_area)),
    [items],
  );
  const lineOptions = useMemo(
    () => uniqueSorted(items.map((i) => i.line_of_therapy)),
    [items],
  );

  const matchingLive = useMemo(() => {
    const key = slug.trim().toLowerCase();
    if (!key) return null;
    return (
      items.find((i) => i.is_published && i.slug.toLowerCase() === key) ||
      items.find((i) => i.slug.toLowerCase() === key) ||
      null
    );
  }, [items, slug]);

  useEffect(() => {
    if (!matchingLive) return;
    setDiseaseArea((prev) => prev.trim() || matchingLive.disease_area || '');
    setLineOfTherapy((prev) => prev.trim() || matchingLive.line_of_therapy || '');
    setIndicationName((prev) => prev.trim() || matchingLive.indication_name || '');
  }, [matchingLive]);

  const onFileChange = async (file: File | null) => {
    setCalendarFile(file);
    if (!file) return;
    try {
      const hints = await peekCalendarCsvHints(file);
      if (hints.suggestedSlug && !slug.trim()) setSlug(hints.suggestedSlug);
      if (hints.suggestedLine && !lineOfTherapy.trim()) setLineOfTherapy(hints.suggestedLine);
      if (hints.title && !diseaseArea.trim()) {
        const area = hints.title
          .replace(/^\d+\s*L\s*\+?\s*/i, '')
          .replace(/\s*catalyst\s*calendar.*/i, '')
          .trim();
        if (area) setDiseaseArea(area);
      }
    } catch {
      /* ignore peek errors */
    }
  };

  const handleVisibility = async (item: CatalogItem, action: 'archive' | 'restore') => {
    const confirmed = window.confirm(
      action === 'archive'
        ? `Hide “${item.title}” (v${item.version_number}) from clients?`
        : `Publish “${item.title}” (v${item.version_number}) as the live version for “${item.slug}”?`,
    );
    if (!confirmed) return;
    setError(null);
    setSuccess(null);
    try {
      setVisibilityBusyId(item.id);
      const response = await apiService.setEmergingCalendarVisibility(item.id, action);
      if (!response?.success) throw new Error(response?.error || 'Visibility update failed');
      setSuccess(
        action === 'archive'
          ? `Hidden “${item.slug}” v${item.version_number}.`
          : `Published “${item.slug}” v${item.version_number}.`,
      );
      await loadList();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Visibility update failed');
    } finally {
      setVisibilityBusyId(null);
    }
  };

  const handleUpload = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setSuccess(null);
    setViewUrl(null);
    if (!calendarFile) {
      setError('Please choose a calendar CSV file.');
      return;
    }
    if (!slug.trim()) {
      setError('Slug is required (e.g. 1l-muc-calendar).');
      return;
    }
    try {
      setUploading(true);
      const response = await apiService.uploadEmergingCalendar({
        calendarCsv: calendarFile,
        slug: slug.trim(),
        disease_area: diseaseArea.trim() || undefined,
        line_of_therapy: lineOfTherapy.trim() || undefined,
        version_label: versionLabel.trim() || undefined,
        indication_name: indicationName.trim() || undefined,
        link_trials: linkTrials,
      });
      if (!response?.success) throw new Error(response?.error || 'Upload failed');
      const stats = response.stats || {};
      const parts = [
        `Published ${stats.slug} as v${stats.version_number}` +
          `${stats.version_label ? ` (${stats.version_label})` : ''}.`,
        `${stats.rows} rows, ${stats.ncts} NCTs (${stats.ncts_linked_to_trials} linked to trials).`,
      ];
      if (stats.ncts_created) {
        parts.push(
          `Created ${stats.ncts_created} new emerging trial(s)` +
            (stats.ncts_enriched_from_ctgov
              ? ` (${stats.ncts_enriched_from_ctgov} enriched from ClinicalTrials.gov)`
              : '') +
            '.',
        );
      }
      if (stats.ncts_refreshed) {
        parts.push(`Refreshed ${stats.ncts_refreshed} existing trial(s) from ClinicalTrials.gov.`);
      }
      if (stats.events_seeded) {
        parts.push(`Seeded ${stats.events_seeded} Trial Tracker alert(s).`);
      }
      if (stats.ncts_already_present) {
        parts.push(`${stats.ncts_already_present} NCT(s) already in 1emerging_trials.`);
      }
      if (Array.isArray(stats.ncts_fetch_failed) && stats.ncts_fetch_failed.length > 0) {
        parts.push(
          `CT.gov lookup failed for ${stats.ncts_fetch_failed.length} NCT(s); inserted from CSV fields.`,
        );
      }
      if (stats.trials_indication_updated) {
        parts.push(
          `Appended indication to ${stats.trials_indication_updated} ongoing trial row(s).`,
        );
      }
      if (stats.indication_created) {
        parts.push(`Created indication “${stats.indication_name}”.`);
      } else if (stats.indication_name) {
        parts.push(`Linked indication “${stats.indication_name}”.`);
      }
      setSuccess(parts.join(' '));
      setViewUrl(response.view_url || `/emerging-calendars/${encodeURIComponent(stats.slug)}`);
      if (stats.disease_area) setDiseaseArea(String(stats.disease_area));
      if (stats.line_of_therapy) setLineOfTherapy(String(stats.line_of_therapy));
      if (stats.indication_name) setIndicationName(String(stats.indication_name));
      await loadList();
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { error?: string } } }).response?.data?.error
          : err instanceof Error
            ? err.message
            : 'Upload failed';
      setError(message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const liveItems = items.filter((i) => i.is_published);
  const archivedItems = items.filter((i) => !i.is_published);

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl border border-gray-100 shadow-card p-6 space-y-2">
        <h1 className="text-2xl font-bold text-slate-900">Catalyst Calendars</h1>
        <p className="text-sm text-slate-500">
          Upload indication catalyst CSVs (Sponsor / Trial / Phase / NCT# / Tier / Data status).
          Missing NCTs are inserted into <code className="text-xs">1emerging_trials</code> and
          enriched from ClinicalTrials.gov. Rows also link to{' '}
          <code className="text-xs">emerging_disease_indications</code>.
        </p>
      </div>

      <form
        onSubmit={handleUpload}
        className="bg-white rounded-2xl border border-gray-100 shadow-card p-6 space-y-4"
      >
        <h2 className="text-lg font-semibold text-slate-900">Upload calendar CSV</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <label className="space-y-1 text-sm md:col-span-2">
            <span className="font-semibold text-slate-700">Calendar CSV</span>
            <input
              type="file"
              accept=".csv,text/csv"
              onChange={(e) => onFileChange(e.target.files?.[0] || null)}
              className="block w-full text-sm"
            />
          </label>
          <label className="space-y-1 text-sm">
            <span className="font-semibold text-slate-700">Slug</span>
            <input
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              placeholder="1l-muc-calendar"
              className="w-full rounded-lg border border-slate-200 px-3 py-2"
              list="calendar-slug-options"
            />
            <datalist id="calendar-slug-options">
              {uniqueSorted(items.map((i) => i.slug)).map((s) => (
                <option key={s} value={s} />
              ))}
            </datalist>
          </label>
          <label className="space-y-1 text-sm">
            <span className="font-semibold text-slate-700">Version label</span>
            <input
              value={versionLabel}
              onChange={(e) => setVersionLabel(e.target.value)}
              placeholder="July 2026"
              className="w-full rounded-lg border border-slate-200 px-3 py-2"
            />
          </label>
          <label className="space-y-1 text-sm">
            <span className="font-semibold text-slate-700">Disease area</span>
            <input
              value={diseaseArea}
              onChange={(e) => setDiseaseArea(e.target.value)}
              placeholder="Urothelial Carcinoma"
              className="w-full rounded-lg border border-slate-200 px-3 py-2"
              list="calendar-disease-options"
            />
            <datalist id="calendar-disease-options">
              {diseaseAreaOptions.map((d) => (
                <option key={d} value={d} />
              ))}
            </datalist>
          </label>
          <label className="space-y-1 text-sm">
            <span className="font-semibold text-slate-700">Line</span>
            <input
              value={lineOfTherapy}
              onChange={(e) => setLineOfTherapy(e.target.value)}
              placeholder="1L"
              className="w-full rounded-lg border border-slate-200 px-3 py-2"
              list="calendar-line-options"
            />
            <datalist id="calendar-line-options">
              {lineOptions.map((d) => (
                <option key={d} value={d} />
              ))}
            </datalist>
          </label>
          <label className="space-y-1 text-sm md:col-span-2">
            <span className="font-semibold text-slate-700">
              Indication name (emerging_disease_indications)
            </span>
            <input
              value={indicationName}
              onChange={(e) => setIndicationName(e.target.value)}
              placeholder="1L la/mUC"
              className="w-full rounded-lg border border-slate-200 px-3 py-2"
              list="calendar-indication-options"
            />
            <datalist id="calendar-indication-options">
              {indications.map((ind) => (
                <option key={ind.id} value={ind.name} />
              ))}
            </datalist>
          </label>
          <label className="inline-flex items-center gap-2 text-sm text-slate-700 md:col-span-2">
            <input
              type="checkbox"
              checked={linkTrials}
              onChange={(e) => setLinkTrials(e.target.checked)}
            />
            Append indication onto matched ongoing trials (by NCT) and create missing NCTs from
            ClinicalTrials.gov
          </label>
        </div>

        {matchingLive && (
          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
            Slug “{matchingLive.slug}” already exists (v{matchingLive.version_number}
            {matchingLive.is_published ? ', live' : ''}). This upload will become the next version.
          </p>
        )}

        {error && (
          <p className="text-sm text-rose-600 bg-rose-50 border border-rose-100 rounded-lg px-3 py-2">
            {error}
          </p>
        )}
        {success && (
          <div className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-2 space-y-1">
            <p>{success}</p>
            {viewUrl && (
              <Link to={viewUrl} className="font-semibold text-indigo-700 hover:underline">
                Open calendar →
              </Link>
            )}
          </div>
        )}

        <button
          type="submit"
          disabled={uploading}
          className="rounded-xl bg-indigo-600 text-white px-4 py-2.5 text-sm font-semibold hover:bg-indigo-700 disabled:opacity-60"
        >
          {uploading ? 'Uploading…' : 'Upload & publish'}
        </button>
      </form>

      <div className="bg-white rounded-2xl border border-gray-100 shadow-card overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-slate-900">Catalog</h2>
        </div>
        {loadingList ? (
          <div className="p-8 text-center text-slate-500 text-sm">Loading…</div>
        ) : items.length === 0 ? (
          <div className="p-8 text-center text-slate-500 text-sm italic">No calendars uploaded yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-4 py-2">Title</th>
                  <th className="px-4 py-2">Slug</th>
                  <th className="px-4 py-2">Indication</th>
                  <th className="px-4 py-2">Rows</th>
                  <th className="px-4 py-2">Version</th>
                  <th className="px-4 py-2">Uploaded</th>
                  <th className="px-4 py-2">Status</th>
                  <th className="px-4 py-2" />
                </tr>
              </thead>
              <tbody>
                {[...liveItems, ...archivedItems].map((item) => (
                  <tr key={item.id} className="border-t border-slate-100">
                    <td className="px-4 py-2 font-medium text-slate-800">{item.title}</td>
                    <td className="px-4 py-2 font-mono text-xs">{item.slug}</td>
                    <td className="px-4 py-2 text-slate-600">{item.indication_name || '—'}</td>
                    <td className="px-4 py-2">{item.row_count ?? '—'}</td>
                    <td className="px-4 py-2">
                      v{item.version_number}
                      {item.version_label ? ` · ${item.version_label}` : ''}
                    </td>
                    <td className="px-4 py-2 text-slate-500">{formatUploadedAt(item.created_at)}</td>
                    <td className="px-4 py-2">
                      <span
                        className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                          item.is_published
                            ? 'bg-emerald-50 text-emerald-700'
                            : 'bg-slate-100 text-slate-500'
                        }`}
                      >
                        {item.is_published ? 'Live' : 'Archived'}
                      </span>
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap space-x-2">
                      <Link
                        to={`/emerging-calendars/${encodeURIComponent(item.slug)}`}
                        className="text-indigo-600 font-semibold hover:underline"
                      >
                        View
                      </Link>
                      <button
                        type="button"
                        disabled={visibilityBusyId === item.id}
                        onClick={() =>
                          handleVisibility(item, item.is_published ? 'archive' : 'restore')
                        }
                        className="text-slate-600 font-semibold hover:underline disabled:opacity-50"
                      >
                        {item.is_published ? 'Hide' : 'Publish'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ManageCatalystCalendars;
